import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import type { Change, UserVoteInfo } from '../types';

interface VoteButtonProps {
  ticket: Change;
  userVoteInfo: UserVoteInfo | null;
  onVoteChange: (changeKey: string, action: string) => Promise<void>;
}

const VoteButton: React.FC<VoteButtonProps> = ({
  ticket,
  userVoteInfo,
  onVoteChange,
}) => {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);

  const userEmail = (
    auth.user?.profile?.email ||
    (auth.user?.profile?.preferred_username as string | undefined)
  )?.toLowerCase();

  const hasVoted =
    userEmail && ticket.votes?.some((v) => v.userEmail.toLowerCase() === userEmail);

  const canVote =
    ticket.isVotingOpen && !hasVoted && userVoteInfo && userVoteInfo.remainingVotes > 0;

  const handleVote = async (): Promise<void> => {
    if (loading) return;
    setLoading(true);
    try {
      await onVoteChange(ticket.key, hasVoted ? 'unvote' : 'vote');
    } catch (err) {
      console.error('Vote error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!ticket.isVotingOpen) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-400">
        <i className="fa-light fa-lock" />
        <span>
          {ticket.voteCount} {ticket.voteCount === 1 ? 'stem' : 'stemmen'}
        </span>
        <span className="text-xs">(gesloten)</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5 text-sm text-neutral-600">
        <i className="fa-light fa-square-check" />
        <span className="font-semibold">{ticket.voteCount}</span>
        <span>{ticket.voteCount === 1 ? 'stem' : 'stemmen'}</span>
      </div>

      {auth.isAuthenticated && (
        <button
          onClick={handleVote}
          disabled={loading || (!hasVoted && !canVote)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            hasVoted
              ? 'bg-primary-100 text-primary-600 border border-primary-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
              : canVote
                ? 'bg-primary-600 text-white hover:bg-primary-500'
                : 'bg-neutral-100 text-neutral-400 cursor-not-allowed hover:text-red-600 hover:shadow-[0_0_0_1px_rgba(239,68,68,0.15)]'
          } ${loading ? 'opacity-50' : ''}`}
          title={
            hasVoted
              ? 'Klik om je stem te verwijderen'
              : canVote
                ? 'Stem voor dit ticket'
                : 'Je hebt geen stemmen meer over'
          }
        >
          {loading ? (
            '...'
          ) : hasVoted ? (
            <span className="flex items-center gap-1">
              <i className="fa fa-thumbs-up" />
              Gestemd
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <i className="fa-light fa-thumbs-up" />
              {canVote ? 'Stem' : 'Alle stemmen opgebruikt'}
            </span>
          )}
        </button>
      )}

      {!auth.isAuthenticated && (
        <span className="text-xs text-neutral-400">
          Meld je aan om te stemmen en contacteer liefst Connect-plus voor dit probleem te
          vermelden
        </span>
      )}
    </div>
  );
};

export default VoteButton;
